// src/pages/ArchiveCentre.tsx — Rebuilt HP3 — Phase 2
// Full document tree, version history, project-linked filing, search, request queue

import { useMemo, useState } from 'react'
import {
  AlertTriangle, Archive, Calendar, CheckCircle, ChevronDown, ChevronRight,
  Clock, Download, FileJson, FileText, Filter, FolderOpen, History,
  Package, Search, Trash2,
} from 'lucide-react'
import { buildAndDownloadArchivePackage } from '../utils/archivePackage'
import {
  clearArchiveRegister, getArchiveRegister,
  removeArchiveRegisterEntry, type ArchiveRegisterEntry,
} from '../utils/archiveRegistry'
import {
  downloadArchiveManifestJson, downloadArchiveRegisterCsv, downloadArchiveSummaryHtml,
} from '../utils/archiveReports'
import {
  MOCK_PROJECT_ARCHIVE_STATUS, MOCK_ARCHIVE_REQUESTS,
  ARCHIVE_OUTPUT_META, archiveReadinessMeta,
  type ArchiveRequest, type ArchiveRequestStatus,
} from '../utils/mockDataArchive'
import { MOCK_JOBS } from '../utils/mockData'
import { formatDate } from '../utils/formatters'

type DocCategory = 'COMMERCIAL' | 'QAQC' | 'DRAWINGS' | 'SITE_RECORDS' | 'SAFETY' | 'HANDOVER'
type DocFormat   = 'PDF' | 'XLSX' | 'DOCX' | 'ZIP' | 'HTML' | 'DWG' | 'CSV'
type DocStatus   = 'CURRENT' | 'SUPERSEDED' | 'DRAFT' | 'ARCHIVED'

interface FiledDoc {
  id: string; jobId: string; category: DocCategory; docType: string
  title: string; version: string; status: DocStatus; format: DocFormat
  filedAt: string; filedByName: string; sizeKb: number; description?: string
  revisions?: Array<{ version: string; filedAt: string; filedByName: string; notes: string }>
}

const CAT_META: Record<DocCategory, { label: string; icon: string; bg: string; text: string; border: string }> = {
  COMMERCIAL:   { label: 'Commercial',   icon: '💼', bg: 'bg-blue-50',   text: 'text-blue-700',   border: 'border-blue-200'   },
  QAQC:         { label: 'QAQC',         icon: '🔍', bg: 'bg-purple-50', text: 'text-purple-700', border: 'border-purple-200' },
  DRAWINGS:     { label: 'Drawings',     icon: '📐', bg: 'bg-teal-50',   text: 'text-teal-700',   border: 'border-teal-200'   },
  SITE_RECORDS: { label: 'Site records', icon: '📋', bg: 'bg-amber-50',  text: 'text-amber-700',  border: 'border-amber-200'  },
  SAFETY:       { label: 'Safety',       icon: '🦺', bg: 'bg-orange-50', text: 'text-orange-700', border: 'border-orange-200' },
  HANDOVER:     { label: 'Handover',     icon: '🏁', bg: 'bg-green-50',  text: 'text-green-700',  border: 'border-green-200'  },
}

const DOC_STATUS_META: Record<DocStatus, { label: string; bg: string; text: string }> = {
  CURRENT:    { label: 'Current',    bg: 'bg-green-100', text: 'text-green-700' },
  SUPERSEDED: { label: 'Superseded', bg: 'bg-gray-100',  text: 'text-gray-500'  },
  DRAFT:      { label: 'Draft',      bg: 'bg-amber-100', text: 'text-amber-700' },
  ARCHIVED:   { label: 'Archived',   bg: 'bg-slate-100', text: 'text-slate-600' },
}

const REQ_STATUS_META: Record<ArchiveRequestStatus, { label: string; bg: string; text: string }> = {
  REQUESTED:  { label: 'Requested',  bg: 'bg-gray-100',   text: 'text-gray-600'   },
  APPROVED:   { label: 'Approved',   bg: 'bg-blue-100',   text: 'text-blue-700'   },
  QUEUED:     { label: 'Queued',     bg: 'bg-amber-100',  text: 'text-amber-700'  },
  GENERATING: { label: 'Generating', bg: 'bg-orange-100', text: 'text-orange-700' },
  READY:      { label: 'Ready',      bg: 'bg-green-100',  text: 'text-green-700'  },
}

const FORMAT_COLORS: Record<DocFormat, string> = {
  PDF:  'bg-red-100 text-red-700',    XLSX: 'bg-green-100 text-green-700',
  DOCX: 'bg-blue-100 text-blue-700',  ZIP:  'bg-amber-100 text-amber-700',
  HTML: 'bg-purple-100 text-purple-700', DWG: 'bg-teal-100 text-teal-700',
  CSV:  'bg-gray-100 text-gray-600',
}

const CAT_ORDER: DocCategory[] = ['COMMERCIAL','QAQC','DRAWINGS','SITE_RECORDS','SAFETY','HANDOVER']

const FILED_DOCS: FiledDoc[] = [
  // J1 — Sandgate Road Signals
  { id:'j1-c1', jobId:'j1', category:'COMMERCIAL', docType:'CONTRACT',  title:'Contract AS4000 — TMR Sandgate Road',       version:'1.0',   status:'CURRENT', format:'PDF', filedAt:'2026-04-15T09:00:00Z', filedByName:'Jane Smith',  sizeKb:842, description:'Signed lump sum contract with TMR. Original execution copy.' },
  { id:'j1-c2', jobId:'j1', category:'COMMERCIAL', docType:'VARIATION', title:'Variation VO-001 — Additional draw pits',   version:'1.0',   status:'CURRENT', format:'PDF', filedAt:'2026-05-10T14:00:00Z', filedByName:'Jane Smith',  sizeKb:218, description:'Approved variation +$18,400 ex GST for 3 additional concrete draw pits.' },
  { id:'j1-c3', jobId:'j1', category:'COMMERCIAL', docType:'CLAIM',     title:'Progress Claim PC-001 — April 2026',        version:'2.0',   status:'CURRENT', format:'PDF', filedAt:'2026-05-03T10:00:00Z', filedByName:'Jane Smith',  sizeKb:312, description:'Certified April claim. Rev 2 corrects GST on variation line.', revisions:[{version:'1.0',filedAt:'2026-05-01T09:00:00Z',filedByName:'Jane Smith',notes:'Initial draft submitted for certification'}] },
  { id:'j1-c4', jobId:'j1', category:'COMMERCIAL', docType:'CLAIM',     title:'Progress Claim PC-002 — May 2026',          version:'1.0',   status:'CURRENT', format:'PDF', filedAt:'2026-06-02T11:00:00Z', filedByName:'Sarah Lee',   sizeKb:298 },
  { id:'j1-c5', jobId:'j1', category:'COMMERCIAL', docType:'PO',        title:'PO-TP001-004 — Olex 6mm Cable 850m',        version:'1.0',   status:'CURRENT', format:'PDF', filedAt:'2026-04-18T08:00:00Z', filedByName:'Sarah Lee',   sizeKb:145 },
  { id:'j1-c6', jobId:'j1', category:'COMMERCIAL', docType:'PO',        title:'PO-TP001-006 — Signal Heads x24',           version:'1.0',   status:'CURRENT', format:'PDF', filedAt:'2026-05-20T08:00:00Z', filedByName:'Sarah Lee',   sizeKb:162 },
  { id:'j1-q1', jobId:'j1', category:'QAQC',       docType:'ITP',       title:'ITP-001 — Sandgate Rd Signals Works',       version:'2.0',   status:'CURRENT', format:'PDF', filedAt:'2026-04-20T09:00:00Z', filedByName:'Tony Rivera', sizeKb:520, description:'14 inspection points, 3 hold points. Rev 2 after client review.', revisions:[{version:'1.0',filedAt:'2026-04-08T08:00:00Z',filedByName:'Tony Rivera',notes:'Initial ITP issued for review'},{version:'2.0',filedAt:'2026-04-20T09:00:00Z',filedByName:'Jane Smith',notes:'Hold points revised after TMR client review — HP3 added for signal head alignment check'}] },
  { id:'j1-q2', jobId:'j1', category:'QAQC',       docType:'SWMS',      title:'SWMS-001 — Traffic signal installation',    version:'1.0',   status:'CURRENT', format:'PDF', filedAt:'2026-04-22T07:30:00Z', filedByName:'Sarah Kim',   sizeKb:380 },
  { id:'j1-q3', jobId:'j1', category:'QAQC',       docType:'SWMS',      title:'SWMS-002 — HV cable trenching and backfill',version:'1.2',   status:'CURRENT', format:'PDF', filedAt:'2026-05-14T07:30:00Z', filedByName:'Sarah Kim',   sizeKb:412, revisions:[{version:'1.0',filedAt:'2026-04-22T07:30:00Z',filedByName:'Sarah Kim',notes:'Initial SWMS'},{version:'1.1',filedAt:'2026-05-02T08:00:00Z',filedByName:'Sarah Kim',notes:'Updated PPE requirements post safety inspection'},{version:'1.2',filedAt:'2026-05-14T07:30:00Z',filedByName:'Tony Rivera',notes:'Added confined space controls for pit entry'}] },
  { id:'j1-q4', jobId:'j1', category:'QAQC',       docType:'PERMIT',    title:'PTW-001 — Energised electrical June 2026',  version:'1.0',   status:'CURRENT', format:'PDF', filedAt:'2026-06-10T06:00:00Z', filedByName:'Tony Rivera', sizeKb:92 },
  { id:'j1-q5', jobId:'j1', category:'QAQC',       docType:'NCR',       title:'NCR-001 — Signal head alignment deviation', version:'1.0',   status:'CURRENT', format:'PDF', filedAt:'2026-06-05T14:00:00Z', filedByName:'Sarah Kim',   sizeKb:78, description:'2 signal heads 3 degrees out of tolerance. CAR issued — re-set and re-test required.' },
  { id:'j1-d1', jobId:'j1', category:'DRAWINGS',   docType:'ELECTRICAL',title:'E-001 Signal Loop Layout — Sandgate Rd',    version:'Rev B', status:'CURRENT', format:'PDF', filedAt:'2026-04-10T09:00:00Z', filedByName:'Design Team', sizeKb:1240, revisions:[{version:'Rev A',filedAt:'2026-03-28T09:00:00Z',filedByName:'Design Team',notes:'For review and comment — preliminary layout'},{version:'Rev B',filedAt:'2026-04-10T09:00:00Z',filedByName:'Design Team',notes:'IFC — issued for construction after TMR comment period closed'}] },
  { id:'j1-d2', jobId:'j1', category:'DRAWINGS',   docType:'CIVIL',     title:'C-001 Cable Route and Trenching Plan',      version:'Rev A', status:'CURRENT', format:'PDF', filedAt:'2026-04-10T09:00:00Z', filedByName:'Design Team', sizeKb:980 },
  { id:'j1-s1', jobId:'j1', category:'SITE_RECORDS',docType:'TIMESHEET',title:'Timesheet — W/E 20 Jun (Tony Rivera)',      version:'1.0',   status:'CURRENT', format:'PDF', filedAt:'2026-06-22T10:00:00Z', filedByName:'Jane Smith',  sizeKb:44 },
  { id:'j1-s2', jobId:'j1', category:'SITE_RECORDS',docType:'TIMESHEET',title:'Timesheet — W/E 20 Jun (Sarah Lee)',        version:'1.0',   status:'CURRENT', format:'PDF', filedAt:'2026-06-22T10:00:00Z', filedByName:'Jane Smith',  sizeKb:41 },
  { id:'j1-s3', jobId:'j1', category:'SITE_RECORDS',docType:'SITE_DIARY',title:'Site Diary — June 2026',                   version:'1.0',   status:'CURRENT', format:'PDF', filedAt:'2026-07-01T09:00:00Z', filedByName:'Tony Rivera', sizeKb:186 },
  { id:'j1-sf1',jobId:'j1', category:'SAFETY',     docType:'TOOLBOX',   title:'Toolbox Talk — Signal installation safety', version:'1.0',   status:'CURRENT', format:'PDF', filedAt:'2026-04-23T06:30:00Z', filedByName:'Sarah Kim',   sizeKb:35 },
  { id:'j1-sf2',jobId:'j1', category:'SAFETY',     docType:'TOOLBOX',   title:'Toolbox Talk — HV isolation procedures',    version:'1.0',   status:'CURRENT', format:'PDF', filedAt:'2026-05-12T06:30:00Z', filedByName:'Sarah Kim',   sizeKb:38 },
  { id:'j1-sf3',jobId:'j1', category:'SAFETY',     docType:'INCIDENT',  title:'Incident Report IR-001 — Near miss cable',  version:'1.0',   status:'CURRENT', format:'PDF', filedAt:'2026-05-30T16:00:00Z', filedByName:'Tony Rivera', sizeKb:62, description:'Near miss — cable drum rolled on slopes. CAR: install wheel chocks at all cable drum storage areas.' },
  // J4 — Energex HV Works (78% ready)
  { id:'j4-c1', jobId:'j4', category:'COMMERCIAL', docType:'CONTRACT',  title:'Contract AS2124 — Energex HV Cable Works',  version:'1.0',   status:'CURRENT', format:'PDF', filedAt:'2026-03-01T09:00:00Z', filedByName:'Mike Walsh',  sizeKb:924 },
  { id:'j4-c2', jobId:'j4', category:'COMMERCIAL', docType:'CLAIM',     title:'Progress Claim PC-001 — March 2026',        version:'1.0',   status:'CURRENT', format:'PDF', filedAt:'2026-04-02T09:00:00Z', filedByName:'Mike Walsh',  sizeKb:244 },
  { id:'j4-c3', jobId:'j4', category:'COMMERCIAL', docType:'CLAIM',     title:'Progress Claim PC-002 — April 2026',        version:'1.0',   status:'CURRENT', format:'PDF', filedAt:'2026-05-02T09:00:00Z', filedByName:'Mike Walsh',  sizeKb:261 },
  { id:'j4-c4', jobId:'j4', category:'COMMERCIAL', docType:'CLAIM',     title:'Progress Claim PC-003 — May 2026',          version:'1.0',   status:'CURRENT', format:'PDF', filedAt:'2026-06-02T09:00:00Z', filedByName:'Mike Walsh',  sizeKb:287 },
  { id:'j4-q1', jobId:'j4', category:'QAQC',       docType:'ITP',       title:'ITP-001 — HV Cable Works 33kV',             version:'1.0',   status:'CURRENT', format:'PDF', filedAt:'2026-03-10T09:00:00Z', filedByName:'Sarah Kim',   sizeKb:610 },
  { id:'j4-q2', jobId:'j4', category:'QAQC',       docType:'SWMS',      title:'SWMS-001 — HV switching and isolation',     version:'2.0',   status:'CURRENT', format:'PDF', filedAt:'2026-04-01T07:30:00Z', filedByName:'Sarah Kim',   sizeKb:448, revisions:[{version:'1.0',filedAt:'2026-03-01T07:30:00Z',filedByName:'Sarah Kim',notes:'Initial SWMS submitted'},{version:'2.0',filedAt:'2026-04-01T07:30:00Z',filedByName:'Sarah Kim',notes:'Updated after Energex safety feedback — isolation sequence revised'}] },
  { id:'j4-q3', jobId:'j4', category:'QAQC',       docType:'NCR',       title:'NCR-001 OPEN — Trench backfill density',    version:'1.0',   status:'CURRENT', format:'PDF', filedAt:'2026-06-15T14:00:00Z', filedByName:'Tony Rivera', sizeKb:84, description:'Backfill compaction failed density test at Sta 4+200. Re-compact and re-test required.' },
  { id:'j4-d1', jobId:'j4', category:'DRAWINGS',   docType:'ELECTRICAL',title:'E-HV-001 HV Cable Route — Energex Works',   version:'Rev C', status:'CURRENT', format:'PDF', filedAt:'2026-03-05T09:00:00Z', filedByName:'Design Team', sizeKb:1480, revisions:[{version:'Rev A',filedAt:'2026-02-10T09:00:00Z',filedByName:'Design Team',notes:'Preliminary design'},{version:'Rev B',filedAt:'2026-02-25T09:00:00Z',filedByName:'Design Team',notes:'Revised — avoid Telstra conduit at Sta 3+450'},{version:'Rev C',filedAt:'2026-03-05T09:00:00Z',filedByName:'Design Team',notes:'IFC — Energex approved final route and joint bay locations'}] },
  { id:'j4-s1', jobId:'j4', category:'SITE_RECORDS',docType:'TIMESHEETS',title:'Timesheet bundle — Mar to Jun 2026',       version:'1.0',   status:'CURRENT', format:'ZIP', filedAt:'2026-06-21T10:00:00Z', filedByName:'Mike Walsh',  sizeKb:312 },
  { id:'j4-sf1',jobId:'j4', category:'SAFETY',     docType:'TOOLBOX',   title:'Toolbox Talk — HV safety refresher',        version:'1.0',   status:'CURRENT', format:'PDF', filedAt:'2026-03-11T06:30:00Z', filedByName:'Sarah Kim',   sizeKb:42 },
  { id:'j4-sf2',jobId:'j4', category:'SAFETY',     docType:'WHS_INSP',  title:'WHS Site Inspection — April 2026',          version:'1.0',   status:'CURRENT', format:'PDF', filedAt:'2026-04-18T14:00:00Z', filedByName:'Sarah Kim',   sizeKb:88 },
  // J5 — Streetlight Upgrade (86% ready, near close-out)
  { id:'j5-c1', jobId:'j5', category:'COMMERCIAL', docType:'CONTRACT',  title:'Contract PANEL SOA — Council Streetlight',   version:'1.0',   status:'CURRENT', format:'PDF', filedAt:'2026-01-15T09:00:00Z', filedByName:'Jane Smith',  sizeKb:716 },
  { id:'j5-c2', jobId:'j5', category:'COMMERCIAL', docType:'CLAIM',     title:'Final Progress Claim — June 2026',           version:'1.0',   status:'CURRENT', format:'PDF', filedAt:'2026-06-28T09:00:00Z', filedByName:'Jane Smith',  sizeKb:319 },
  { id:'j5-q1', jobId:'j5', category:'QAQC',       docType:'ITP',       title:'ITP-001 — Streetlight Installation Complete',version:'3.0',   status:'CURRENT', format:'PDF', filedAt:'2026-06-20T09:00:00Z', filedByName:'Tony Rivera', sizeKb:548, description:'All 14 hold points released. Final ITP ready for handover.', revisions:[{version:'1.0',filedAt:'2026-01-20T09:00:00Z',filedByName:'Tony Rivera',notes:'Initial ITP'},{version:'2.0',filedAt:'2026-03-01T09:00:00Z',filedByName:'Tony Rivera',notes:'Revised after stage 1 completion'},{version:'3.0',filedAt:'2026-06-20T09:00:00Z',filedByName:'Jane Smith',notes:'FINAL — all hold points released, ready for handover'}] },
  { id:'j5-q2', jobId:'j5', category:'QAQC',       docType:'SWMS',      title:'SWMS-001 — Elevated work platform ops',      version:'1.0',   status:'CURRENT', format:'PDF', filedAt:'2026-01-22T07:30:00Z', filedByName:'Sarah Kim',   sizeKb:396 },
  { id:'j5-d1', jobId:'j5', category:'DRAWINGS',   docType:'AS_BUILT',  title:'As-Built E-001 — Streetlight Final Layout',  version:'RevAB', status:'CURRENT', format:'PDF', filedAt:'2026-06-25T09:00:00Z', filedByName:'Design Team', sizeKb:1640, description:'As-built drawings with GPS coordinates for all luminaire bases. Ready for council GIS.' },
  { id:'j5-h1', jobId:'j5', category:'HANDOVER',   docType:'HANDOVER',  title:'Handover Pack — Streetlight Upgrade',        version:'DRAFT', status:'DRAFT',   format:'PDF', filedAt:'2026-06-28T16:00:00Z', filedByName:'Jane Smith',  sizeKb:2840, description:'Close-out handover pack. DRAFT pending final lux test photos from site.' },
  { id:'j5-h2', jobId:'j5', category:'HANDOVER',   docType:'OM_MANUAL', title:'O&M Manual — Sylvania LED Luminaires',       version:'1.0',   status:'CURRENT', format:'PDF', filedAt:'2026-06-20T09:00:00Z', filedByName:'Design Team', sizeKb:1180, description:'Manufacturer O&M manual including maintenance schedule and fault code tables.' },
  { id:'j5-sf1',jobId:'j5', category:'SAFETY',     docType:'TOOLBOX',   title:'Toolbox bundle — all talks June 2026',       version:'1.0',   status:'CURRENT', format:'ZIP', filedAt:'2026-06-28T10:00:00Z', filedByName:'Sarah Kim',   sizeKb:280 },
]

function fmtSize(kb: number) { return kb >= 1024 ? (kb / 1024).toFixed(1) + ' MB' : kb + ' KB' }

// ─── Overview Tab ─────────────────────────────────────────────────

function OverviewTab() {
  const [expandedJob, setExpandedJob] = useState<string | null>(null)
  const metrics = {
    total:   MOCK_PROJECT_ARCHIVE_STATUS.length,
    ready:   MOCK_PROJECT_ARCHIVE_STATUS.filter(s => s.archiveReadyPct >= 80).length,
    due:     MOCK_PROJECT_ARCHIVE_STATUS.filter(s => s.nextSnapshotDueDate === '2026-06-30').length,
    pending: MOCK_ARCHIVE_REQUESTS.filter(r => r.status !== 'READY').length,
  }
  return (
    <div className="space-y-5">
      <div className="grid grid-cols-2 xl:grid-cols-4 gap-3">
        {([
          ['Projects tracked',     metrics.total,   'text-gray-900'  ],
          ['Archive ready (80%+)', metrics.ready,   'text-green-700' ],
          ['Snapshots due 30 Jun', metrics.due,     'text-amber-700' ],
          ['Open requests',        metrics.pending, 'text-blue-700'  ],
        ] as const).map(([label, value, color]) => (
          <div key={label} className="rounded-xl border border-gray-200 bg-white px-4 py-3">
            <div className="text-xs text-gray-500 mb-1">{label}</div>
            <div className={'text-2xl font-bold ' + color}>{value}</div>
          </div>
        ))}
      </div>
      <div className="space-y-3">
        {MOCK_PROJECT_ARCHIVE_STATUS.map(s => {
          const job  = MOCK_JOBS.find(j => j.id === s.jobId)
          const rm   = archiveReadinessMeta(s.archiveReadyPct)
          const open = expandedJob === s.jobId
          return (
            <div key={s.jobId} className="bg-white border border-gray-200 rounded-xl overflow-hidden">
              <button onClick={() => setExpandedJob(open ? null : s.jobId)}
                className="w-full px-5 py-4 flex items-center gap-4 hover:bg-gray-50 text-left">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap mb-1">
                    <span className="font-semibold text-gray-900">{job?.jobNumber ?? s.jobId}</span>
                    <span className={'text-xs px-2 py-0.5 rounded-full font-medium ' + rm.bg + ' ' + rm.text}>{rm.label}</span>
                    {s.nextSnapshotDueDate === '2026-06-30' && <span className="text-xs bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded-full">Snapshot due</span>}
                  </div>
                  <p className="text-xs text-gray-500 truncate">{job?.description ?? ''}</p>
                </div>
                <div className="w-44 flex-shrink-0">
                  <div className="flex justify-between text-xs text-gray-500 mb-1">
                    <span>Archive readiness</span><span className="font-bold">{s.archiveReadyPct}%</span>
                  </div>
                  <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                    <div className={'h-full rounded-full ' + (s.archiveReadyPct >= 80 ? 'bg-green-500' : s.archiveReadyPct >= 65 ? 'bg-amber-400' : 'bg-gray-400')} style={{ width: s.archiveReadyPct + '%' }} />
                  </div>
                </div>
                {open ? <ChevronDown className="w-4 h-4 text-gray-400 flex-shrink-0" /> : <ChevronRight className="w-4 h-4 text-gray-400 flex-shrink-0" />}
              </button>
              {open && (
                <div className="px-5 pb-5 border-t border-gray-100 space-y-4">
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 pt-4">
                    {([['Records',s.recordsCoveragePct],['QAQC',s.qaqcCoveragePct],['Commercial',s.commercialClosePct],['Handover',s.handoverClosePct]] as const).map(([label, pct]) => (
                      <div key={label} className="border border-gray-200 rounded-xl p-3">
                        <div className="flex justify-between text-xs mb-1">
                          <span className="text-gray-500">{label}</span>
                          <span className={'font-semibold ' + (pct >= 80 ? 'text-green-600' : pct >= 60 ? 'text-amber-600' : 'text-gray-500')}>{pct}%</span>
                        </div>
                        <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
                          <div className={'h-full rounded-full ' + (pct >= 80 ? 'bg-green-500' : pct >= 60 ? 'bg-amber-400' : 'bg-gray-400')} style={{ width: pct + '%' }} />
                        </div>
                      </div>
                    ))}
                  </div>
                  {s.blockingItems.length > 0 && (
                    <div>
                      <div className="text-xs font-semibold text-gray-600 mb-2">Blocking items</div>
                      {s.blockingItems.map(item => (
                        <div key={item} className="flex items-start gap-2 text-xs text-gray-600 mb-1.5">
                          <AlertTriangle className="w-3.5 h-3.5 text-amber-500 flex-shrink-0 mt-0.5" />{item}
                        </div>
                      ))}
                    </div>
                  )}
                  <div className="text-xs text-blue-700 bg-blue-50 border border-blue-200 rounded-lg px-3 py-2">{s.recommendedAction}</div>
                  <div className="flex gap-4 text-xs text-gray-400 flex-wrap">
                    {s.lastSnapshotAt ? <span className="flex items-center gap-1"><Calendar className="w-3 h-3" />Last snapshot: {formatDate(s.lastSnapshotAt)}</span> : <span>No snapshot yet</span>}
                    {s.nextSnapshotDueDate && <span className="flex items-center gap-1"><Clock className="w-3 h-3" />Next due: {s.nextSnapshotDueDate}</span>}
                  </div>
                </div>
              )}
            </div>
          )
        })}
      </div>
    </div>
  )
}

// ─── Documents Tab ─────────────────────────────────────────────────

function DocumentsTab() {
  const [selectedJob, setSelectedJob] = useState('j1')
  const [search, setSearch]           = useState('')
  const [expandedCat, setExpandedCat] = useState<DocCategory | null>(null)
  const [expandedDoc, setExpandedDoc] = useState<string | null>(null)

  const jobsWithDocs = useMemo(() => {
    const ids = [...new Set(FILED_DOCS.map(d => d.jobId))]
    return MOCK_JOBS.filter(j => ids.includes(j.id))
  }, [])

  const q = search.trim().toLowerCase()

  const docsForJob = useMemo(() => FILED_DOCS.filter(d => {
    if (d.jobId !== selectedJob) return false
    if (!q) return true
    return d.title.toLowerCase().includes(q) || d.docType.toLowerCase().includes(q) || (d.description ?? '').toLowerCase().includes(q)
  }), [selectedJob, q])

  const byCategory = useMemo(() => {
    const map: Partial<Record<DocCategory, FiledDoc[]>> = {}
    for (const doc of docsForJob) {
      if (!map[doc.category]) map[doc.category] = []
      map[doc.category]!.push(doc)
    }
    return map
  }, [docsForJob])

  return (
    <div className="flex gap-4 items-start">
      <div className="w-52 flex-shrink-0 space-y-1">
        <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide px-2 mb-2">Projects</div>
        {jobsWithDocs.map(job => {
          const cnt    = FILED_DOCS.filter(d => d.jobId === job.id).length
          const st     = MOCK_PROJECT_ARCHIVE_STATUS.find(s => s.jobId === job.id)
          const rm     = st ? archiveReadinessMeta(st.archiveReadyPct) : null
          const active = selectedJob === job.id
          return (
            <button key={job.id} onClick={() => { setSelectedJob(job.id); setExpandedCat(null); setExpandedDoc(null) }}
              className={'w-full text-left px-3 py-2.5 rounded-xl transition-colors ' + (active ? 'bg-[#1F3864] text-white' : 'hover:bg-gray-100 text-gray-700')}>
              <div className="font-semibold text-sm">{job.jobNumber}</div>
              <div className="flex justify-between mt-0.5">
                <span className={'text-xs ' + (active ? 'text-blue-200' : 'text-gray-400')}>{cnt} docs</span>
                {rm && st && <span className={'text-[10px] px-1.5 py-0.5 rounded-full font-medium ' + (active ? 'bg-white/20 text-white' : rm.bg + ' ' + rm.text)}>{st.archiveReadyPct}%</span>}
              </div>
            </button>
          )
        })}
      </div>

      <div className="flex-1 min-w-0 space-y-3">
        <div className="flex items-center gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input className="input pl-9 text-sm" placeholder="Search documents..." value={search} onChange={e => setSearch(e.target.value)} />
          </div>
          <span className="text-xs text-gray-400 flex-shrink-0">{docsForJob.length} documents</span>
        </div>

        {docsForJob.length === 0 && (
          <div className="bg-white border border-gray-200 rounded-xl py-12 text-center text-gray-400">
            <FolderOpen className="w-8 h-8 mx-auto mb-2 text-gray-300" />
            <p className="text-sm">{q ? 'No documents match your search' : 'No documents filed for this project'}</p>
          </div>
        )}

        {CAT_ORDER.filter(cat => byCategory[cat]?.length).map(cat => {
          const docs = byCategory[cat]!
          const cm   = CAT_META[cat]
          const open = expandedCat === cat || !!q
          return (
            <div key={cat} className={'border rounded-xl overflow-hidden ' + cm.border}>
              <button onClick={() => !q && setExpandedCat(open && !q ? null : cat)}
                className={'w-full px-4 py-3 flex items-center gap-3 ' + cm.bg}>
                <span className="text-base">{cm.icon}</span>
                <span className={'font-semibold text-sm ' + cm.text}>{cm.label}</span>
                <span className={'text-xs px-2 py-0.5 rounded-full bg-white/60 ' + cm.text}>{docs.length}</span>
                {!q && (open ? <ChevronDown className={'w-4 h-4 ml-auto ' + cm.text} /> : <ChevronRight className={'w-4 h-4 ml-auto ' + cm.text} />)}
              </button>
              {(open || !!q) && (
                <div className="bg-white divide-y divide-gray-100">
                  {docs.map(doc => {
                    const sm      = DOC_STATUS_META[doc.status]
                    const docOpen = expandedDoc === doc.id
                    return (
                      <div key={doc.id}>
                        <div className="px-4 py-3 flex items-center gap-3 hover:bg-gray-50 cursor-pointer" onClick={() => setExpandedDoc(docOpen ? null : doc.id)}>
                          <FileText className="w-4 h-4 text-gray-400 flex-shrink-0" />
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="text-sm font-medium text-gray-900 truncate">{doc.title}</span>
                              <span className={'text-[10px] px-1.5 py-0.5 rounded font-medium ' + sm.bg + ' ' + sm.text}>{sm.label}</span>
                              <span className={'text-[10px] px-1.5 py-0.5 rounded font-mono ' + FORMAT_COLORS[doc.format]}>{doc.format}</span>
                            </div>
                            <div className="text-xs text-gray-400 mt-0.5 flex gap-3 flex-wrap">
                              <span>v{doc.version}</span>
                              <span>{formatDate(doc.filedAt)}</span>
                              <span>{doc.filedByName}</span>
                              <span>{fmtSize(doc.sizeKb)}</span>
                              {doc.revisions && <span className="flex items-center gap-0.5 text-indigo-500"><History className="w-3 h-3" />{doc.revisions.length} rev{doc.revisions.length !== 1 ? 's' : ''}</span>}
                            </div>
                          </div>
                          <button className="btn-secondary text-xs py-1 px-2 flex-shrink-0" onClick={e => e.stopPropagation()}><Download className="w-3 h-3" /></button>
                          {docOpen ? <ChevronDown className="w-4 h-4 text-gray-400 flex-shrink-0" /> : <ChevronRight className="w-4 h-4 text-gray-400 flex-shrink-0" />}
                        </div>
                        {docOpen && (
                          <div className="px-5 pb-4 bg-gray-50 border-t border-gray-100 space-y-3 pt-3">
                            {doc.description && <p className="text-xs text-gray-600">{doc.description}</p>}
                            {doc.revisions && (
                              <div>
                                <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2 flex items-center gap-1.5"><History className="w-3.5 h-3.5" />Version history</div>
                                <div className="space-y-1.5">
                                  {doc.revisions.map((rev, i) => (
                                    <div key={i} className="flex items-start gap-3 text-xs">
                                      <span className="font-mono bg-gray-200 text-gray-600 px-1.5 py-0.5 rounded flex-shrink-0">v{rev.version}</span>
                                      <div><span className="text-gray-400">{formatDate(rev.filedAt)} · {rev.filedByName}</span><div className="text-gray-700 mt-0.5">{rev.notes}</div></div>
                                    </div>
                                  ))}
                                  <div className="flex items-start gap-3 text-xs">
                                    <span className="font-mono bg-[#1F3864] text-white px-1.5 py-0.5 rounded flex-shrink-0">v{doc.version}</span>
                                    <div><span className="text-gray-400">{formatDate(doc.filedAt)} · {doc.filedByName}</span><div className="text-green-700 font-medium mt-0.5">Current version</div></div>
                                  </div>
                                </div>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    )
                  })}
                </div>
              )}
            </div>
          )
        })}
      </div>
    </div>
  )
}

// ─── Requests Tab ──────────────────────────────────────────────────

function RequestsTab() {
  const [requests, setRequests] = useState<ArchiveRequest[]>(MOCK_ARCHIVE_REQUESTS)
  const ORDER: ArchiveRequestStatus[] = ['REQUESTED','APPROVED','QUEUED','GENERATING','READY']
  const NEXT_LABEL: Record<ArchiveRequestStatus, string> = { REQUESTED:'Approve', APPROVED:'Queue', QUEUED:'Generate', GENERATING:'Mark ready', READY:'Download' }

  const advance = (id: string) => setRequests(prev => prev.map(r => {
    if (r.id !== id) return r
    const idx = ORDER.indexOf(r.status)
    const nxt = idx < ORDER.length - 1 ? ORDER[idx + 1] : r.status
    return { ...r, status: nxt, readyAt: nxt === 'READY' ? new Date().toISOString() : r.readyAt }
  }))

  return (
    <div className="space-y-5">
      <div className="bg-white border border-gray-200 rounded-xl p-4">
        <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">Pipeline</div>
        <div className="flex items-center gap-1">
          {ORDER.map((s, i) => {
            const cnt = requests.filter(r => r.status === s).length
            const rm  = REQ_STATUS_META[s]
            return (
              <div key={s} className="flex items-center gap-1 flex-1">
                <div className={'flex-1 rounded-lg px-3 py-2 text-center ' + (cnt > 0 ? rm.bg + ' ' + rm.text : 'bg-gray-50 text-gray-400')}>
                  <div className="text-lg font-bold">{cnt}</div>
                  <div className="text-[10px] font-medium">{rm.label}</div>
                </div>
                {i < ORDER.length - 1 && <ChevronRight className="w-4 h-4 text-gray-300 flex-shrink-0" />}
              </div>
            )
          })}
        </div>
      </div>

      {requests.filter(r => r.status !== 'READY').length > 0 && (
        <div>
          <div className="text-sm font-semibold text-gray-700 mb-2">Open requests</div>
          <div className="space-y-3">
            {requests.filter(r => r.status !== 'READY').map(req => {
              const job = MOCK_JOBS.find(j => j.id === req.jobId)
              const rm  = REQ_STATUS_META[req.status]
              return (
                <div key={req.id} className="bg-white border border-gray-200 rounded-xl p-4">
                  <div className="flex items-start justify-between gap-4 flex-wrap">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap mb-1">
                        <span className="font-semibold text-gray-900">{job?.jobNumber}</span>
                        <span className={'text-xs px-2 py-0.5 rounded-full font-medium ' + rm.bg + ' ' + rm.text}>{rm.label}</span>
                        <span className="text-xs bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded-full">{req.requestType.replace(/_/g,' ')}</span>
                      </div>
                      <p className="text-sm text-gray-600">{req.targetPeriodLabel}</p>
                      <p className="text-xs text-gray-400 mt-0.5">Requested {formatDate(req.requestedAt)} by {req.requestedByName}</p>
                      {req.notes && <p className="text-xs text-gray-500 italic mt-1">{req.notes}</p>}
                      <div className="flex flex-wrap gap-1 mt-2">
                        {req.outputs.map(o => <span key={o} className="text-[10px] bg-gray-100 text-gray-600 px-1.5 py-0.5 rounded">{ARCHIVE_OUTPUT_META[o].label}</span>)}
                      </div>
                    </div>
                    <button onClick={() => advance(req.id)} className="btn-primary text-xs flex-shrink-0">{NEXT_LABEL[req.status]} →</button>
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      )}

      {requests.filter(r => r.status === 'READY').length > 0 && (
        <div>
          <div className="text-sm font-semibold text-gray-700 mb-2">Ready for download</div>
          <div className="space-y-3">
            {requests.filter(r => r.status === 'READY').map(req => {
              const job = MOCK_JOBS.find(j => j.id === req.jobId)
              return (
                <div key={req.id} className="bg-green-50 border border-green-200 rounded-xl p-4 flex items-start justify-between gap-4 flex-wrap">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                      <CheckCircle className="w-4 h-4 text-green-600" />
                      <span className="font-semibold text-gray-900">{job?.jobNumber}</span>
                      <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full font-medium">Ready</span>
                    </div>
                    <p className="text-sm text-gray-600">{req.targetPeriodLabel}</p>
                    {req.readyAt && <p className="text-xs text-gray-400 mt-0.5">Ready {formatDate(req.readyAt)}</p>}
                  </div>
                  <button className="btn-primary text-xs flex items-center gap-1.5 flex-shrink-0"><Download className="w-3.5 h-3.5" />Download pack</button>
                </div>
              )
            })}
          </div>
        </div>
      )}
    </div>
  )
}

// ─── Register Tab ──────────────────────────────────────────────────

function RegisterTab({ setFeedback }: { setFeedback: (s: string | null) => void }) {
  const [entries, setEntries] = useState<ArchiveRegisterEntry[]>(() => getArchiveRegister())
  const [search, setSearch]   = useState('')
  const [typeFilter, setType] = useState('ALL')

  const filtered = useMemo(() => entries.filter(e => {
    const q  = search.trim().toLowerCase()
    const ms = !q || e.jobNumber.toLowerCase().includes(q) || e.jobDescription.toLowerCase().includes(q)
    return ms && (typeFilter === 'ALL' || e.requestType === typeFilter)
  }), [entries, search, typeFilter])

  const rebuild = (entry: ArchiveRegisterEntry) => {
    try {
      const r = buildAndDownloadArchivePackage(entry.jobId, entry.archiveRequestSnapshot ?? undefined)
      setEntries(getArchiveRegister())
      setFeedback('Archive rebuilt: ' + r.filename)
    } catch (err) {
      setFeedback(err instanceof Error ? err.message : 'Rebuild failed.')
    }
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-48">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input className="input pl-9 text-sm" placeholder="Search job, project, label..." value={search} onChange={e => setSearch(e.target.value)} />
        </div>
        <div className="flex items-center gap-2">
          <Filter className="w-4 h-4 text-gray-400" />
          <select className="input text-sm w-auto" value={typeFilter} onChange={e => setType(e.target.value)}>
            <option value="ALL">All types</option>
            <option value="PROJECT_ARCHIVE">Project archive</option>
            <option value="MONTH_END_SNAPSHOT">Month-end snapshot</option>
            <option value="YEAR_END_SNAPSHOT">Year-end snapshot</option>
          </select>
        </div>
        <div className="flex gap-2 ml-auto">
          <button onClick={() => { downloadArchiveSummaryHtml(filtered); setFeedback('Summary HTML exported') }} disabled={filtered.length === 0} className="btn-secondary text-xs flex items-center gap-1.5"><FileText className="w-3.5 h-3.5" />Summary</button>
          <button onClick={() => { downloadArchiveRegisterCsv(filtered); setFeedback('CSV exported') }} disabled={filtered.length === 0} className="btn-secondary text-xs flex items-center gap-1.5"><Download className="w-3.5 h-3.5" />CSV</button>
          <button onClick={() => { downloadArchiveManifestJson(filtered); setFeedback('Manifest JSON exported') }} disabled={filtered.length === 0} className="btn-secondary text-xs flex items-center gap-1.5"><FileJson className="w-3.5 h-3.5" />JSON</button>
          <button onClick={() => { clearArchiveRegister(); setEntries([]) }} className="btn-secondary text-xs flex items-center gap-1.5"><Trash2 className="w-3.5 h-3.5" />Clear</button>
        </div>
      </div>
      <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
        {filtered.length === 0 ? (
          <div className="py-14 text-center text-gray-400">
            <Package className="w-8 h-8 mx-auto mb-2 text-gray-300" />
            <p className="text-sm">No archive packs in the local register</p>
            <p className="text-xs mt-1">Generated packages appear here. Use Jobs page to create one.</p>
          </div>
        ) : (
          <div className="divide-y divide-gray-100">
            {filtered.map(entry => (
              <div key={entry.id} className="px-5 py-4">
                <div className="flex items-start justify-between gap-4 flex-wrap">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                      <span className="font-semibold text-gray-900">{entry.jobNumber}</span>
                      <span className="text-xs bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded-full">{entry.requestType.replace(/_/g,' ')}</span>
                      {entry.archiveReadyPct != null && <span className="text-xs bg-green-100 text-green-700 px-1.5 py-0.5 rounded-full">{entry.archiveReadyPct}% ready</span>}
                    </div>
                    <p className="text-sm text-gray-600 truncate">{entry.jobDescription}</p>
                    <div className="flex gap-4 text-xs text-gray-400 mt-1.5 flex-wrap">
                      <span className="flex items-center gap-1"><Calendar className="w-3 h-3" />{formatDate(entry.generatedAt)}</span>
                      <span className="flex items-center gap-1"><FileText className="w-3 h-3" />{entry.filename}</span>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <button onClick={() => rebuild(entry)} className="btn-secondary text-xs flex items-center gap-1.5"><Download className="w-3.5 h-3.5" />Rebuild</button>
                    <button onClick={() => { removeArchiveRegisterEntry(entry.id); setEntries(getArchiveRegister()) }} className="btn-secondary text-xs flex items-center gap-1.5"><Trash2 className="w-3.5 h-3.5" />Remove</button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

// ─── Main component ────────────────────────────────────────────────

type Tab = 'overview' | 'documents' | 'requests' | 'register'

export function ArchiveCentre() {
  const [tab, setTab]           = useState<Tab>('overview')
  const [feedback, setFeedback] = useState<string | null>(null)
  const openRequests = MOCK_ARCHIVE_REQUESTS.filter(r => r.status !== 'READY').length
  const readyPacks   = MOCK_ARCHIVE_REQUESTS.filter(r => r.status === 'READY').length

  return (
    <div className="space-y-5">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Archive Centre</h1>
          <p className="text-sm text-gray-500 mt-0.5">Document tree · version history · project-linked filing · archive request queue</p>
        </div>
        <div className="flex gap-2 flex-wrap text-xs">
          <span className="bg-gray-100 text-gray-600 px-2.5 py-1.5 rounded-lg">{FILED_DOCS.length} documents filed</span>
          {openRequests > 0 && <span className="bg-amber-100 text-amber-700 px-2.5 py-1.5 rounded-lg">{openRequests} open request{openRequests !== 1 ? 's' : ''}</span>}
          {readyPacks  > 0 && <span className="bg-green-100 text-green-700 px-2.5 py-1.5 rounded-lg">{readyPacks} pack{readyPacks !== 1 ? 's' : ''} ready</span>}
        </div>
      </div>

      {feedback && (
        <div className="rounded-xl border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-800 flex items-center justify-between gap-3">
          <span>{feedback}</span>
          <button onClick={() => setFeedback(null)} className="text-green-500 hover:text-green-700 text-lg leading-none">×</button>
        </div>
      )}

      <div className="flex gap-1 bg-gray-100 rounded-xl p-1">
        {([
          { key: 'overview',  label: 'Overview'  },
          { key: 'documents', label: 'Documents (' + FILED_DOCS.length + ')' },
          { key: 'requests',  label: 'Requests' + (openRequests > 0 ? ' (' + openRequests + ')' : '') },
          { key: 'register',  label: 'Register'  },
        ] as const).map(t => (
          <button key={t.key} onClick={() => setTab(t.key)}
            className={'flex-1 text-sm font-medium py-2 px-3 rounded-lg transition-colors ' + (tab === t.key ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700')}>
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'overview'  && <OverviewTab />}
      {tab === 'documents' && <DocumentsTab />}
      {tab === 'requests'  && <RequestsTab />}
      {tab === 'register'  && <RegisterTab setFeedback={setFeedback} />}
    </div>
  )
}
